import { DashboardUtils } from 'src/app/dashboards/DashboardUtils';
import { Metric } from 'src/app/dashboards/shared/dashboard-config/dashboard-config.model';
import { MenuItem } from 'src/app/layout/app.menu.items';
import { getProductMenuItems } from 'src/app/state/products/product-menu-items';

/**
 * Product to open a slice's dashboard under: the selected product when its menu links the
 * dashboard, else the first accessible product whose menu does, else the selected product.
 * The last case is a dashboard no menu links any more — the route guard still lets saved
 * slices through, and dashboards are routed under every product.
 */
export function resolveSliceProductId(
  dashboardId: string,
  selectedProductId: number,
  accessibleProductIds: readonly number[],
  hasClaims: (claims: string[]) => boolean,
): number {
  let metricId: Metric;
  try {
    metricId = DashboardUtils.decodeDashboardId(dashboardId).metricId as Metric;
  } catch {
    return selectedProductId;
  }

  const candidates = [
    selectedProductId,
    ...accessibleProductIds.filter((id) => id !== selectedProductId),
  ];
  return (
    candidates.find((productId) =>
      menuLinksDashboard(getProductMenuItems(productId, metricId), dashboardId, hasClaims),
    ) ?? selectedProductId
  );
}

/** Same reachability rule as the menu itself: visible, enabled, and allowed by the user's claims. */
function menuLinksDashboard(
  items: MenuItem[],
  dashboardId: string,
  hasClaims: (claims: string[]) => boolean,
): boolean {
  return items.some((item) => {
    if (item.visible === false || item.disabled) {
      return false;
    }
    if (item.requiredClaims?.length && !hasClaims(item.requiredClaims)) {
      return false;
    }
    const link = Array.isArray(item.routerLink) ? item.routerLink[0] : item.routerLink;
    if (typeof link === 'string' && link.startsWith('/dashboards/') && link.split('/')[3] === dashboardId) {
      return true;
    }
    return item.items ? menuLinksDashboard(item.items, dashboardId, hasClaims) : false;
  });
}
